import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Box, Grow, Portal, styled } from '@mui/material';
import AlphaFeedToast, { AlphaFeedToastVariant } from './AlphaFeedToast';
import {
    ALPHAFEED_TOAST_DURATION_MS,
    ALPHAFEED_TOAST_MAX_VISIBLE,
} from '@/pages/HypertrackerAlphafeed/alphafeedConstants';

/** Mobile deck: below the 54px mobile header (+8px), each newer toast shifted by this much. */
const MOBILE_TOP_OFFSET_PX = 62;
const MOBILE_DECK_STEP_PX = 14;
/** Desktop: clears the Zendesk help launcher (main.scss `iframe#launcher`: bottom 35px + ~50px). */
const DESKTOP_BOTTOM_OFFSET_PX = 100;

export interface AlphafeedToastOptions {
    variant: AlphaFeedToastVariant;
    title: string;
    subtitle?: string;
    /** Hyperliquid coin; shows its icon in the `info` variant. */
    coin?: string;
    /** Auto-hide delay; `null` keeps the toast until it is updated or closed. */
    duration?: number | null;
}

interface AlphafeedToastItem extends AlphafeedToastOptions {
    id: number;
}

interface AlphafeedToastContextValue {
    showToast: (options: AlphafeedToastOptions) => number;
    /** Replaces a toast's content in place; re-adds it if it was already closed or dropped. */
    updateToast: (id: number, options: AlphafeedToastOptions) => void;
    closeToast: (id: number) => void;
}

const AlphafeedToastContext = createContext<AlphafeedToastContextValue>({
    showToast: () => -1,
    updateToast: () => {},
    closeToast: () => {},
});

export const useAlphafeedToast = () => useContext(AlphafeedToastContext);

const ToastStack = styled(Box)(({ theme }) => ({
    position: 'fixed',
    bottom: `${DESKTOP_BOTTOM_OFFSET_PX}px`,
    right: '24px',
    zIndex: theme.zIndex.snackbar,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: 'min(403px, calc(100vw - 32px))',
    // Clicks pass through the empty stack area; each toast re-enables them.
    pointerEvents: 'none',
    // Mobile: a deck under the header, all toasts in one grid cell, newest in front.
    [theme.breakpoints.down('md')]: {
        top: `${MOBILE_TOP_OFFSET_PX}px`,
        bottom: 'auto',
        left: '16px',
        right: '16px',
        width: 'auto',
        display: 'grid',
        '& > *': { gridArea: '1 / 1' },
    },
}));

const DeckItem = styled('div', { shouldForwardProp: (prop) => prop !== 'deckIndex' })<{
    deckIndex: number;
}>(({ theme, deckIndex }) => ({
    [theme.breakpoints.down('md')]: {
        // `translate` keeps Grow's `transform` free for its own animation.
        translate: `0 ${deckIndex * MOBILE_DECK_STEP_PX}px`,
    },
}));

/**
 * Stacked toasts of AlphaFeed (trading and wallet setup), mounted once in the Hypertracker layout:
 * bottom-right on desktop, a deck under the header on mobile; oldest dropped past the limit.
 * Separate from the global `ToastContext`, which shows one single-line toast at a time.
 */
export const AlphafeedToastProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [toasts, setToasts] = useState<AlphafeedToastItem[]>([]);
    const nextId = useRef(0);
    const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

    const clearTimer = useCallback((id: number) => {
        clearTimeout(timers.current.get(id));
        timers.current.delete(id);
    }, []);

    const closeToast = useCallback(
        (id: number) => {
            clearTimer(id);
            setToasts((prev) => prev.filter((toast) => toast.id !== id));
        },
        [clearTimer],
    );

    const startTimer = useCallback(
        (id: number, duration: number | null | undefined) => {
            clearTimer(id);
            if (duration === null) return;
            timers.current.set(
                id,
                setTimeout(() => closeToast(id), duration ?? ALPHAFEED_TOAST_DURATION_MS),
            );
        },
        [clearTimer, closeToast],
    );

    const upsertToast = useCallback(
        (id: number, options: AlphafeedToastOptions) => {
            startTimer(id, options.duration);
            setToasts((prev) => {
                if (prev.some((toast) => toast.id === id)) {
                    return prev.map((toast) => (toast.id === id ? { ...options, id } : toast));
                }
                const next = [...prev, { ...options, id }];
                next.slice(0, -ALPHAFEED_TOAST_MAX_VISIBLE).forEach((toast) =>
                    clearTimer(toast.id),
                );
                return next.slice(-ALPHAFEED_TOAST_MAX_VISIBLE);
            });
        },
        [startTimer, clearTimer],
    );

    const showToast = useCallback(
        (options: AlphafeedToastOptions) => {
            const id = nextId.current++;
            upsertToast(id, options);
            return id;
        },
        [upsertToast],
    );

    useEffect(() => {
        const activeTimers = timers.current;
        return () => activeTimers.forEach((timer) => clearTimeout(timer));
    }, []);

    const value = useMemo(
        () => ({ showToast, updateToast: upsertToast, closeToast }),
        [showToast, upsertToast, closeToast],
    );

    return (
        <AlphafeedToastContext.Provider value={value}>
            {children}
            {toasts.length > 0 && (
                <Portal>
                    <ToastStack
                        sx={{ pb: { xs: `${(toasts.length - 1) * MOBILE_DECK_STEP_PX}px`, md: 0 } }}
                    >
                        {toasts.map((toast, index) => (
                            <Grow key={toast.id} in appear>
                                <DeckItem deckIndex={index}>
                                    <AlphaFeedToast
                                        variant={toast.variant}
                                        title={toast.title}
                                        subtitle={toast.subtitle}
                                        coin={toast.coin}
                                        onClose={() => closeToast(toast.id)}
                                    />
                                </DeckItem>
                            </Grow>
                        ))}
                    </ToastStack>
                </Portal>
            )}
        </AlphafeedToastContext.Provider>
    );
};
